import { AppNode, EdgeData } from "@features/builder/graph/types"
import { createEdgesLookup } from "@features/builder/NodeDrawer/NodeForm/GraphNav/createEdgesLookup"
import {
  getItemColor,
  removeParentEdge,
} from "@features/builder/NodeDrawer/NodeForm/GraphNav/helpers"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { IconButton, List, ListItem, ListItemText } from "@mui/material"
import { Edge, useEdges, useNodes, useReactFlow } from "@xyflow/react"
import { useMemo } from "react"
import { getSortedNodeLabelsById } from "@features/graph/utils/graphNodeUtils"
import { Remove } from "@mui/icons-material"
import { GraphNavAddEdge } from "@features/builder/NodeDrawer/NodeForm/GraphNav/GraphNavAddEdge"
import { useGoToNode } from "@features/builder/hooks/useGoToNode"
import { CreateEdgesOpenButton } from "@features/builder/CreateEdges/CreateEdgesOpenButton"

export const GraphNavParents = (props: { node: AppNode; disabled: boolean }) => {
  const reactFlowInstance = useReactFlow<AppNode, Edge<EdgeData>>()
  const nodes = useNodes<AppNode>()
  const edges = useEdges<Edge<EdgeData>>()
  const currentNodeId = props.node.id

  const lookup = useMemo(() => createEdgesLookup(edges), [edges])
  const parents = useMemo(
    () => lookup.childrens[currentNodeId] ?? [],
    [currentNodeId, lookup.childrens],
  )

  const goToNode = useGoToNode()

  const sortedNodeLabelsById = getSortedNodeLabelsById(nodes, parents)

  const handleRemoveEdge = (id: string) => {
    removeParentEdge(id, props.node, nodes, reactFlowInstance)
  }

  return (
    <NodeDrawerAccordion id="graph-nav-parents" label="Parents:" expand={false}>
      <CreateEdgesOpenButton
        nodeId={props.node.id}
        sourceNodeIds={sortedNodeLabelsById.map(({ id }) => id)}
        label="Create edges with parents"
        disabled={props.disabled}
      />
      <List sx={{ p: 0 }}>
        {sortedNodeLabelsById.map(({ label, id }) => {
          return (
            <ListItem
              divider
              key={id}
              sx={{
                backgroundColor: getItemColor(id),
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(225, 225, 225, 0.4)",
                },
              }}
            >
              <ListItemText primary={label} onClick={() => goToNode(id)} />
              <IconButton disabled={props.disabled} onClick={() => handleRemoveEdge(id)}>
                <Remove />
              </IconButton>
            </ListItem>
          )
        })}
        <GraphNavAddEdge disabled={props.disabled} node={props.node} mode="parents" />
      </List>
    </NodeDrawerAccordion>
  )
}
